import { useState } from "react";
import { useListEvents } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { buildCalendarLinks, triggerIcsDownload } from "@/lib/calendar";
import { Calendar, MapPin, ExternalLink, CalendarDays, CalendarPlus, Download } from "lucide-react";
import { format, parseISO } from "date-fns";

type EventType = "conference" | "webinar" | "seminar" | "workshop";

const TYPE_LABELS: Record<EventType, string> = {
  conference: "Conference",
  webinar: "Webinar",
  seminar: "Seminar",
  workshop: "Workshop",
};

const TYPE_COLORS: Record<EventType, string> = {
  conference: "bg-teal-100 text-teal-800 border-teal-200",
  webinar: "bg-indigo-100 text-indigo-800 border-indigo-200",
  seminar: "bg-amber-100 text-amber-800 border-amber-200",
  workshop: "bg-rose-100 text-rose-800 border-rose-200",
};

const FILTER_TABS: Array<{ value: EventType | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "conference", label: "Conferences" },
  { value: "webinar", label: "Webinars" },
  { value: "seminar", label: "Seminars" },
  { value: "workshop", label: "Workshops" },
];

function formatDateRange(date: string, endDate?: string | null): string {
  const start = parseISO(date);
  if (!endDate) return format(start, "d MMM yyyy");
  const end = parseISO(endDate);
  if (format(start, "MMMyyyy") === format(end, "MMMyyyy")) {
    return `${format(start, "d")}–${format(end, "d MMM yyyy")}`;
  }
  return `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}`;
}

interface EventsListProps {
  intro?: string;
  accentBg?: string;
  accentIcon?: string;
  accentLink?: string;
}

export function EventsList({
  intro = "BSR conferences, EULAR congresses, RCN/RCP/GPhC/CSP/RCOT events, NRAS webinars, and more — UK rheumatology CPD and professional development events in one place.",
  accentBg = "bg-teal-100",
  accentIcon = "text-teal-700",
  accentLink = "text-teal-600 hover:text-teal-800",
}: EventsListProps) {
  const [activeType, setActiveType] = useState<EventType | "all">("all");
  const [showPast, setShowPast] = useState(false);

  const { data: events, isLoading, error } = useListEvents(
    activeType !== "all" ? { type: activeType } : {},
  );

  if (isLoading) return <LoadingScreen />;
  if (error || !events) return <div className="text-slate-500">Unable to load events.</div>;

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  const filteredEvents = events.filter(e => {
    const eventDate = parseISO(e.date);
    const isEventPast = eventDate < today;
    return showPast ? true : !isEventPast;
  });

  const pastCount = events.filter(e => parseISO(e.date) < today).length;

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className={`${accentBg} p-2.5 rounded-xl`}>
            <CalendarDays className={`h-6 w-6 ${accentIcon}`} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Upcoming Events</h2>
        </div>
        <p className="text-slate-600 max-w-2xl">{intro}</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveType(tab.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeType === tab.value
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {pastCount > 0 && (
          <button
            onClick={() => setShowPast(p => !p)}
            className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
          >
            {showPast ? "Hide past events" : `Show past events (${pastCount})`}
          </button>
        )}
      </div>

      {filteredEvents.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <CalendarDays className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No events found</p>
          <p className="text-sm mt-1">
            {showPast ? "No events match the selected filter." : "No upcoming events — try showing past events."}
          </p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredEvents.map(event => {
            const isPastEvent = parseISO(event.date) < today;
            const { googleUrl, icsBlob } = buildCalendarLinks(event);
            return (
              <div
                key={event.id}
                className={`bg-white rounded-2xl border shadow-sm flex flex-col p-6 transition-shadow hover:shadow-md ${
                  isPastEvent ? "opacity-60 border-slate-200" : "border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${TYPE_COLORS[event.type as EventType]}`}>
                    {TYPE_LABELS[event.type as EventType]}
                  </span>
                  {isPastEvent && (
                    <span className="text-xs text-slate-400 font-medium">Past</span>
                  )}
                  {event.isFeatured && !isPastEvent && (
                    <span className="text-xs text-teal-600 font-semibold">Featured</span>
                  )}
                </div>

                <h3 className="text-sm font-semibold text-slate-900 mb-1 leading-snug">{event.title}</h3>
                <p className="text-xs text-slate-500 mb-3 font-medium">{event.organiser}</p>

                {event.description && (
                  <p className="text-xs text-slate-600 leading-relaxed mb-4 flex-1 line-clamp-3">
                    {event.description}
                  </p>
                )}

                <div className="mt-auto space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    <span>{formatDateRange(event.date, event.endDate)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span>{event.location ?? "Online"}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
                  <a
                    href={event.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${accentLink}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Visit website
                  </a>

                  {!isPastEvent && (
                    <>
                      <a
                        href={googleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        <CalendarPlus className="h-3.5 w-3.5" />
                        Add to Calendar
                      </a>
                      <button
                        onClick={() => triggerIcsDownload(
                          `${event.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.ics`,
                          icsBlob,
                        )}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        <Download className="h-3.5 w-3.5" />
                        ↓ .ics
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
